"use client"

import { useEffect, useSyncExternalStore } from "react"
import { isTestMode } from "@ahead-health/core/system-checks/testMode"
import { posthog } from "@/lib/posthog"
import { readServedVariants } from "@/lib/served-variants"
import { useExperimentRegion } from "@/lib/edit/experiment-region-registry"

/**
 * Plaintext sibling of the Vercel Flags SDK's `vercel-flag-overrides` cookie,
 * written by middleware (see `proxy.ts`) whenever the encrypted override
 * cookie is present. Lets these hooks honor the same override the server
 * sees — for purely-client-side flags that don't go through `precompute()`.
 *
 * Not security-relevant: anyone with devtools can already call
 * `posthog.featureFlags.override()` to force a variant for their own session.
 * The encrypted cookie remains the auth boundary for anything the server
 * decides (page routes, precompute variants).
 */
const CLIENT_FLAG_OVERRIDES_COOKIE = "ahead-client-flag-overrides"

/**
 * Per-flag localStorage override: `ahead-flag:<flag>` → `"test"` / `"control"`
 * / `"true"` / `"false"`. Set from devtools to flip a variant for QA without
 * touching PostHog or the FlagsSDK registry. Survives reloads (the cookie
 * sibling can't, because middleware clears any plaintext override that
 * isn't backed by an encrypted parent cookie). Anyone with devtools could
 * already call `posthog.featureFlags.override()`, so this is no weaker.
 */
const LOCAL_STORAGE_PREFIX = "ahead-flag:"

/**
 * Server-assignment cookie written by proxy.ts. Contains a URL-encoded JSON
 * map of `{ [flag]: variant }` assigned at the edge before the page is served.
 * Reading it client-side lets the navbar (and any other experiment component)
 * resolve its variant synchronously on first paint — before the
 * idle-deferred PostHog SDK init fires (up to 3 s later).
 */
const EXPERIMENT_ASSIGNMENTS_COOKIE = "ahead-experiment-assignments"

export function readClientFlagOverride(
  flag: string
): string | boolean | undefined {
  if (typeof document === "undefined") return undefined
  // localStorage access can throw in private mode, sandboxed iframes, or
  // when a host page has shadowed Storage with a non-standard polyfill.
  // Wrap in try/catch so the cookie path remains the reliable fallback.
  try {
    if (
      typeof localStorage !== "undefined" &&
      typeof localStorage.getItem === "function"
    ) {
      const ls = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${flag}`)
      if (ls === "true") return true
      if (ls === "false") return false
      if (ls !== null) return ls
    }
  } catch {
    // ignore — fall through to cookie path
  }
  // Explicit QA / E2E override (proxy.ts decrypts `vercel-flag-overrides` into
  // this plaintext cookie; present only when a live override exists this
  // request). Must win over the server-assigned default below so a forced
  // variant — the Vercel toolbar or the Playwright `forcedFlags` fixture — is
  // honored instead of the edge's natural bucketing.
  const prefix = `${CLIENT_FLAG_OVERRIDES_COOKIE}=`
  const match = document.cookie.split("; ").find((c) => c.startsWith(prefix))
  if (match) {
    try {
      const values = JSON.parse(
        decodeURIComponent(match.slice(prefix.length))
      ) as Record<string, string | boolean>
      if (values[flag] !== undefined) return values[flag]
    } catch {
      // fall through to the server-assigned cookie / PostHog
    }
  }
  // Server-assigned variant (written by proxy.ts at the edge for the
  // client-read experiments). Resolves the navbar/pricing synchronously on
  // first paint, decoupled from the idle-deferred SDK init — but below the QA
  // override so forcing still wins, and below localStorage for the same reason.
  const assignPrefix = `${EXPERIMENT_ASSIGNMENTS_COOKIE}=`
  const assignMatch = document.cookie
    .split("; ")
    .find((c) => c.startsWith(assignPrefix))
  if (assignMatch) {
    try {
      const values = JSON.parse(
        decodeURIComponent(assignMatch.slice(assignPrefix.length))
      ) as Record<string, string>
      if (values[flag] !== undefined) return values[flag]
    } catch {
      // fall through to PostHog
    }
  }
  // Cookieless server-chosen variant (visitor without experiment consent):
  // the edge sent it as a Server-Timing metric on the document response.
  // Below the QA/override paths and the cookie, above PostHog.
  const served = readServedVariants()[flag]
  if (served !== undefined) return served
  return undefined
}

/**
 * Hook to get the current variant for an experiment flag.
 *
 * Returns the variant key (e.g. `"control"`, `"test"`) or `undefined` while
 * loading (when no initial value is provided).
 *
 * Uses useSyncExternalStore for React 18+ concurrent mode safety.
 *
 * **Flicker prevention:** pass `initialVariant` when the server component
 * has already evaluated the flag (via `getExperimentVariant()`). The hook
 * returns `initialVariant` until the PostHog SDK finishes loading, then
 * switches to the live value — which should match when the same
 * `ahead-experiment-id` is used on server and client.
 *
 * **Resolution order:** `localStorage` (`ahead-flag:<flag>`, QA) →
 * `ahead-client-flag-overrides` cookie (QA/E2E override, decrypted by
 * middleware from `?vercel-flag-overrides=…`) → `ahead-experiment-assignments`
 * cookie (server-assigned variant, written by proxy.ts at the edge for
 * synchronous, flicker-free first paint) → PostHog. The first match wins, so
 * explicit overrides beat the edge assignment, which beats PostHog. (The
 * assignment cookie carries the same variant PostHog would return for this
 * `ahead-experiment-id`, so it's a synchronous stand-in, not a different value.)
 */
export function useExperimentVariant(
  flag: string,
  initialVariant?: string
): string | undefined {
  return useExperimentVariantInternal(flag, initialVariant, true)
}

/**
 * Same resolution as {@link useExperimentVariant} but does NOT fire the
 * `$feature_flag_called` exposure event. Use when you need to *read* a user's
 * assigned arm somewhere that isn't the experiment's exposure surface — e.g.
 * stamping the arm onto an analytics/email payload — so reading it here doesn't
 * dilute the experiment population with users who never saw the variant.
 */
export function useExperimentVariantNoExposure(
  flag: string,
  initialVariant?: string
): string | undefined {
  return useExperimentVariantInternal(flag, initialVariant, false)
}

/** One `$feature_flag_called` per flag per page load — module-level so a
 *  remount (or React strict-mode's double effect invoke) can't double-fire. */
const reportedExposures = new Set<string>()

/**
 * The arm to REPORT as an edge-assigned experiment's exposure, resolved from the
 * authoritative synchronous source — the `ahead-experiment-assignments` cookie
 * (or a QA override), the same source the no-flash `<head>` script reads — NOT a
 * React render snapshot.
 *
 * Why not the render value: the membership-first navbar lives in a shared,
 * statically-rendered layout (`PageShell`) that reads the flag with no
 * server-seeded `initialVariant`. A static layout has no per-user cookie
 * server-side, so its `useSyncExternalStore` hydration snapshot is structurally
 * `"control"`, and the exposure effect can fire off that stale snapshot before
 * the store re-renders to the cookie value — permanently cross-bucketing real
 * arm visitors into control (AHD-8246: 311 control vs 24 arm within 6h of the
 * deploy). Reading the cookie at fire time reports the true arm regardless of
 * React's render/effect ordering.
 *
 * Falls back to PostHog's live flag only if the cookie is somehow absent, and
 * returns `undefined` (→ wait) until an arm is knowable, so the once-per-load
 * dedup is never burned on an unresolved read.
 */
function resolveExposureArm(flag: string): string | undefined {
  const assigned = readClientFlagOverride(flag)
  if (assigned === true) return "test"
  if (assigned === false) return "control"
  if (typeof assigned === "string") return assigned
  const live = posthog.getFeatureFlag(flag, { send_event: false })
  if (live === undefined || live === false) return undefined
  if (live === true) return "test"
  return String(live)
}

/**
 * Explicitly emit the `$feature_flag_called` exposure for an edge-assigned
 * experiment. posthog-js only auto-fires the exposure from `getFeatureFlag()`,
 * and the assignment cookie short-circuits that read (see
 * `readClientFlagOverride`), so the exposure would otherwise never register.
 *
 * `enabled` gates the exposure to the experiment's eligible population (e.g.
 * membership-first is CH/AT-only — NL/DE must never join); it must ONLY be true
 * on a surface that mounts for that population, so the exposure isn't fired for
 * every visitor by the PostHog bootstrap. The reported arm is resolved from the
 * cookie via {@link resolveExposureArm}, not passed in — a stale render snapshot
 * must never reach the exposure (see that helper). No-op in test mode, when
 * `enabled` is false, and until an arm is knowable.
 */
export function useReportExperimentExposure(
  flag: string,
  enabled: boolean
): void {
  useEffect(() => {
    if (!enabled) return
    if (isTestMode()) return
    if (reportedExposures.has(flag)) return

    // PostHog init is deferred (requestIdleCallback in PostHogProvider), and the
    // consent banner may briefly opt-out at load — so a direct capture() on
    // mount can no-op before the SDK is loaded (or while opted-out). Marking
    // `reportedExposures` first, as this once did, dropped the exposure
    // permanently and never retried. Instead defer to `onFeatureFlags` (fires
    // only post-init, and immediately if flags are already loaded) and only
    // record once it can actually be sent AND the arm is known.
    const fire = () => {
      if (reportedExposures.has(flag)) return
      if (posthog.has_opted_out_capturing()) return
      const arm = resolveExposureArm(flag)
      if (arm === undefined) return
      reportedExposures.add(flag)
      posthog.capture("$feature_flag_called", {
        $feature_flag: flag,
        $feature_flag_response: arm,
      })
    }
    posthog.onFeatureFlags(fire)
  }, [flag, enabled])
}

function useExperimentVariantInternal(
  flag: string,
  initialVariant: string | undefined,
  sendExposure: boolean
): string | undefined {
  const testModeDefault = initialVariant ?? "control"
  return useSyncExternalStore(
    (callback) => {
      // PostHog emits this when flags are loaded/updated
      posthog.onFeatureFlags(callback)
      return () => {} // posthog-js doesn't return an unsubscribe, but the component unmount handles cleanup
    },
    () => {
      if (isTestMode()) return testModeDefault
      const override = readClientFlagOverride(flag)
      if (override !== undefined) {
        if (override === true) return "test"
        if (override === false) return initialVariant
        return String(override)
      }
      const variant = posthog.getFeatureFlag(
        flag,
        sendExposure ? undefined : { send_event: false }
      )
      if (variant === undefined || variant === false) return initialVariant
      if (variant === true) return "test" // boolean flag
      return String(variant)
    },
    () => testModeDefault // server snapshot
  )
}

// `getFeatureFlagResult` returns a fresh object on every call, so reading
// `.payload` directly would hand `useSyncExternalStore` a new reference each
// render and trip React's "getSnapshot should be cached" infinite loop. Cache
// the payload per flag and only swap the reference when the serialized value
// actually changes, keeping the snapshot stable between renders.
const payloadSnapshotCache = new Map<string, { json: string; value: unknown }>()

function getStableFlagPayload(flag: string): unknown {
  const raw = posthog.getFeatureFlagResult(flag, { send_event: false })?.payload
  const json = JSON.stringify(raw ?? null)
  const cached = payloadSnapshotCache.get(flag)
  if (cached && cached.json === json) return cached.value
  payloadSnapshotCache.set(flag, { json, value: raw })
  return raw
}

/**
 * Experiment payload JSON from PostHog (e.g. tunable thresholds on a variant).
 * Reads the same `@/lib/posthog` singleton as `useExperimentVariant`.
 */
export function useFeatureFlagPayload(flag: string): unknown {
  return useSyncExternalStore(
    (callback) => {
      posthog.onFeatureFlags(callback)
      return () => {}
    },
    () => getStableFlagPayload(flag),
    () => undefined
  )
}

export function useBooleanFeatureFlag(
  flag: string,
  initialValue = false
): boolean {
  return useSyncExternalStore(
    (callback) => {
      posthog.onFeatureFlags(callback)
      return () => {}
    },
    () => {
      if (isTestMode()) return initialValue
      const override = readClientFlagOverride(flag)
      if (override !== undefined) return override === true
      const value = posthog.getFeatureFlag(flag)
      if (value === undefined) return initialValue
      return value === true
    },
    () => initialValue
  )
}

/**
 * Declarative experiment component. Renders the matching variant's content.
 *
 * Usage:
 *   <Experiment
 *     flag="hero-cta-copy"
 *     variants={{
 *       control: <Button>Book your check-up</Button>,
 *       test: <Button>Get started for CHF 490</Button>,
 *     }}
 *   />
 *
 * For flicker-free rendering on pages that run the experiment, evaluate the
 * variant server-side via `getExperimentVariant()` and pass it as
 * `initialVariant`. Without `initialVariant`, falls back to the `control`
 * variant while the PostHog SDK loads.
 */
export function Experiment({
  flag,
  variants,
  initialVariant,
}: {
  flag: string
  variants: Record<string, React.ReactNode>
  initialVariant?: string
}) {
  const variant = useExperimentVariant(flag, initialVariant)
  const key = variant ?? "control"
  const arm = variants[key] ?? variants.control ?? null
  const ref = useExperimentRegion(flag, key)
  // display:contents → zero layout effect; no data-* ships in public HTML
  // (the inline editor injects data-experiment/data-variant on activation).
  return (
    <span ref={ref} style={{ display: "contents" }}>
      {arm}
    </span>
  )
}
